import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import io from "socket.io-client";
import { useAccount } from "wagmi";
import { enqueueSnackbar } from "notistack";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";

// Create context
const SocketContext = createContext(null);

export const SocketProvider = ({ children }) => {
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [ticketUnreadMap, setTicketUnreadMap] = useState({});
  const [currentTicket, setCurrentTicket] = useState({});
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const { address } = useAccount();
  const [socket, setSocket] = useState(null);

  const socketRef = useRef(null);
  const navigate = useNavigate();

  // Function to get the appropriate socket URL based on environment
  const getSocketUrl = () => {
    // Try the dedicated socket endpoint first
    const socketUrl = process.env.REACT_APP_SOCKET_ENDPOINT;
    if (socketUrl) return socketUrl;

    // Fall back to API endpoint if socket endpoint is not set
    const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
    if (apiEndpoint) {
      return apiEndpoint.replace("/v1/", "");
    }

    // Last resort fallback
    return "https://dev-default-dot-bayccollection.ue.r.appspot.com";
  };

  useEffect(() => {
    if (currentTicket?.id) {
      setTicketUnreadMap((prev) => {
        const updated = { ...prev };
        delete updated[currentTicket.id];
        return updated;
      });
    }
  }, [currentTicket]);

  const showNewMessageAlert = useCallback(
    (ticketId, message) => {
      if (message && ticketId !== currentTicket?.id) {
        enqueueSnackbar(message, {
          variant: "info",
          action: (snackbarId) => (
            <Button
              color="secondary"
              onClick={() => {
                navigate(`/tickets/${ticketId}`);
              }}
            >
              View Message
            </Button>
          ),
        });

        // also update the unread map to notify the user
        setTicketUnreadMap((prev) => ({ ...prev, [ticketId]: true }));
      }
    },
    [currentTicket, navigate]
  );

  const showTicketAlert = useCallback(
    (data) => {
      // ignore if the ticket is already open
      if (data && data.ticketId === currentTicket?.id) {
        return;
      }

      if (data && data.message) {
        enqueueSnackbar(data.message, {
          variant: "info",
          action: (snackbarId) => (
            <Button
              color="secondary"
              onClick={() => {
                navigate("/tickets");
              }}
            >
              View
            </Button>
          ),
        });
      }
    },
    [currentTicket, navigate]
  );

  useEffect(() => {
    if (!address) {
      return; // Don't connect if no user is logged in
    }

    const SOCKET_URL = getSocketUrl();
    console.log("Connecting to socket URL:", SOCKET_URL);

    // Match server configuration with client settings
    const socket = io(SOCKET_URL, {
      transports: ["polling", "websocket"], // Match server configuration
      path: "/socket.io/", // Explicitly set path to match server
      credentials: true, // Enable credentials
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000, // Match server timeout
      // Include auth token if available
      auth: {
        token: localStorage.getItem("jwtToken"), // Make sure to use the correct key where your token is stored
      },
    });

    socketRef.current = socket;
    setSocket(socket);

    // Add connection event handlers
    socket.on("connect", () => {
      console.log(
        "Connected to socket:",
        socket.id,
        "via",
        socket.io.engine.transport.name
      );
      setConnectionStatus("connected");

      // Join notification channel
      socket.emit("join-notifications");

      // Send a health check to verify connection is fully working
      socket.emit("health-check", (response) => {
        console.log("Socket health check response:", response);
      });

      // Ping the server periodically to keep connection alive
      // This is especially important for Azure deployments
      const pingInterval = setInterval(() => {
        socket.emit("ping", { clientTime: Date.now() }, (response) => {
          console.log("Ping response:", response);
        });
      }, 30000);

      // Clean up interval on disconnect
      socket.on("disconnect", () => clearInterval(pingInterval));
    });

    socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
      setConnectionStatus("error");

      // Show user-friendly error in development
      if (process.env.NODE_ENV === "development") {
        enqueueSnackbar(`Socket connection error: ${error.message}`, {
          variant: "error",
          autoHideDuration: 5000,
        });
      }
    });

    socket.on("disconnect", (reason) => {
      console.log("Socket disconnected, reason:", reason);
      setConnectionStatus("disconnected");
    });

    socket.on("error", (error) => {
      console.error("Socket error:", error);
      setConnectionStatus("error");
    });

    socket.on("notifications-updated", ({ unreadCount }) => {
      console.log("Notification updated: => ", unreadCount);
      setUnreadNotificationCount(unreadCount);
    });

    // Specific notification events
    socket.on("ticket-updated", (data) => {
      console.log("Ticket updated: => ", data);
      showTicketAlert(data);
    });

    socket.on("ticket-message", (ticketMsg) => {
      console.log("Ticket message received: => ", ticketMsg);
      showNewMessageAlert(ticketMsg.ticketId, ticketMsg.message);
    });

    socket.on("new-message", (newMsg) => {
      console.log("new message received: => ", newMsg);
      setCurrentTicket((prev) => {
        if (prev.id === newMsg.ticketId) {
          return {
            ...prev,
            messages: prev?.messages?.find((m) => m.id === newMsg.id)
              ? prev.messages
              : [...(prev.messages || []), newMsg],
          };
        } else {
          // just update the unread map to notify the user
          setTicketUnreadMap((prev) => ({ ...prev, [newMsg.ticketId]: true }));
          return prev;
        }
      });
    });

    return () => {
      socket.disconnect();
    };
  }, [address, showNewMessageAlert, showTicketAlert]);

  // Show connection status in both development and production
  // until issues are resolved
  const ConnectionDebug = () => {
    return (
      <div
        style={{
          position: "fixed",
          bottom: 10,
          right: 10,
          padding: "5px 10px",
          background: connectionStatus === "connected" ? "#e6ffe6" : "#ffe6e6",
          border: `1px solid ${
            connectionStatus === "connected" ? "#99cc99" : "#cc9999"
          }`,
          borderRadius: "4px",
          fontSize: "12px",
          zIndex: 9999,
        }}
      >
        Socket: {connectionStatus}
      </div>
    );
  };

  return (
    <SocketContext.Provider
      value={{
        socket,
        connectionStatus,
        unreadNotificationCount,
        setUnreadNotificationCount,
        ticketUnreadMap,
        currentTicket,
        setCurrentTicket,
      }}
    >
      {children}
      <ConnectionDebug />
    </SocketContext.Provider>
  );
};

export const useSocketContext = () => useContext(SocketContext);
